import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { imageService } from '@bedrock/fido-sdk';
import { DEFAULT_IMAGE_FORMAT, IMAGE_FIT_SCALE_CROP, IMAGE_QUALITY } from '@bedrock-rdk/constants';
import { t } from '../../i18n';
import { Overlay } from '../../player/interface/overlay/overlay.component';
import { Button } from '../../tornado/button/button.component';
import { cover } from '../../tornado/utils/textures';
import { SELECTED_SIGNAL } from '../../tornado/signals';
import connect from './nextVideo.connector';
import { CLOSE_SIGNAL, NEXT_VIDEO_SIGNAL, REPLAY_VIDEO_SIGNAL, NEXT_VIDEO_COUNTDOWN } from './nextVideo.constants';
import { sendEvent } from '../../../logic/analytics/analytics.utils';
import { NextVideoItem } from './nextVideoItem/nextVideoItem.component';
import { getInitialStyle, defaultContext, GAP } from './nextVideo.style';
import { getVideoItemWithProgress, makeVideoItem } from './nextVideo.utils';
import { getCenter } from '../../tornado/utils/sizes';

export class NextVideo extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        Overlay: {
          type: Overlay,
        },
        ProgramImage: {},
        BackButton: {
          type: Button,
          label: t('global.back'),
          picto: {
            name: 'ArrowLeft',
          },
        },
        Content: {
          Current: {
            type: NextVideoItem,
            label: t('player.nextVideo.askReplay'),
            signals: {
              [SELECTED_SIGNAL]: '_handleCurrentVideoSelected',
            },
          },
          Next: {
            type: NextVideoItem,
            signals: {
              [SELECTED_SIGNAL]: '_handleNextVideoSelected',
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Content extends this {
        $enter() {
          this._updateDefaultFocus();
        }

        set currentVideo(value) {
          super.currentVideo = value;

          this._updateDefaultFocus();
        }

        set nextVideo(value) {
          super.nextVideo = value;

          this._updateDefaultFocus();
        }

        _active() {
          super._active();
          this._updateDefaultFocus();
        }

        _updateDefaultFocus() {
          this._focused = [this.tag('Next'), this.tag('Current')].find(({ visible }) => visible);
        }

        _handleLeft() {
          if (this.tag('Current').visible) {
            this._focused = this.tag('Current');
          }

          return true;
        }

        _handleRight() {
          if (this.tag('Next').visible) {
            this._focused = this.tag('Next');
          }

          return true;
        }

        _handleUp() {
          this._setState('BackButton');

          return true;
        }
      },
      class BackButton extends this {
        _getFocused() {
          return this.tag('BackButton');
        }

        _handleEnter() {
          this.signal(CLOSE_SIGNAL);
          sendEvent({ key: 'backNextVideo' });

          return true;
        }

        _handleDown() {
          this._setState('Content');

          return true;
        }
      },
    ];
  }

  _construct() {
    this._style = defaultContext;
    this._withWatchAgain = false;
    this._reset();
  }

  _active() {
    clearInterval(this._intervalId);
    this.countdown = NEXT_VIDEO_COUNTDOWN;
    this._intervalId = setInterval(() => {
      this.countdown -= 1;

      if (this._countdown <= 0) {
        clearInterval(this._intervalId);
      }
    }, 1000);
    this._setState('Content');
  }

  _attach() {
    this._unsubscribe = connect(this);
    this.tag('Current').visible = false;
    this.tag('Next').visible = false;
  }

  _inactive() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
    }
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._reset();
  }

  _handleNextVideoSelected(item) {
    sendEvent({ key: 'playNextVideo' });

    this.signal(NEXT_VIDEO_SIGNAL, item);
  }

  _handleCurrentVideoSelected(item) {
    sendEvent({ key: 'replayNextVideo' });

    this.signal(REPLAY_VIDEO_SIGNAL, item);
  }

  set watchAgain(value) {
    this._withWatchAgain = value;
    this._setCurrentVideoAsItem();
  }

  set currentVideo(value) {
    this._currentVideoItem = value;
    this._setCurrentVideoAsItem();

    const imageId = _get(value, 'image.id', '');
    this._setProgramImage(imageId);
  }

  set nextVideo(value) {
    this._nextVideoItem = value;
    this._setVideoPosterContent('Next', value);
  }

  get isAvailable() {
    return this._withWatchAgain || this.tag('Next').visible;
  }

  get countdown() {
    return this._countdown;
  }

  set countdown(value) {
    this._countdown = Math.max(value, 0);

    this.tag('Next').label = [t('player.nextVideo.nextCountdown'), this._countdown, t('player.nextVideo.unit')].join(
      ' ',
    );

    if (this._countdown <= 0 && this._nextVideoItem) {
      this.signal(NEXT_VIDEO_SIGNAL, this._nextVideoItem);
      sendEvent({ key: 'autoPlayNextVideo' });
    }
  }

  set visible(value) {
    super.visible = value;

    if (value) {
      sendEvent({ key: 'endscreenDisplayed' });
    }
  }

  get visible() {
    return super.visible;
  }

  _setProgramImage(imageId) {
    if (!imageId) {
      this.tag('ProgramImage').texture = null;

      return;
    }

    const { finalW, finalH } = this;
    this.tag('ProgramImage').texture = cover(
      imageService.getUrl(imageId, finalW, finalH, {
        fit: IMAGE_FIT_SCALE_CROP,
        quality: IMAGE_QUALITY,
        format: DEFAULT_IMAGE_FORMAT,
      }),
      finalW,
      finalH,
    );
  }

  _setVideoPosterContent(tag, content) {
    const view = this.tag(tag);
    if (content) {
      view.visible = true;
      view.item = makeVideoItem(content, this._style.screenSize);
    } else {
      view.visible = false;
    }
    this._updateViewsPositions();
  }

  _updateViewsPositions() {
    const views = [this.tag('Current'), this.tag('Next')].filter(({ visible }) => visible);
    const gap = GAP[this._style.screenSize];
    let totalWidth = 0;
    views.forEach(view => {
      view.x = totalWidth;
      totalWidth += view.w + gap;
    });
    this.tag('Content').x = getCenter(this.w, totalWidth - gap);
  }

  _setCurrentVideoAsItem() {
    if (this._withWatchAgain && this._currentVideoItem) {
      // Current video should have 100% progress since it's fully watched
      this._setVideoPosterContent('Current', getVideoItemWithProgress(this._currentVideoItem, 100));
    }
  }

  _reset() {
    this._focused = null;
    this._withWatchAgain = false;
  }

  _getFocused() {
    return this._focused;
  }

  _handleBack() {
    sendEvent({ key: 'backNextVideo' });

    return false;
  }
}
